import { Box, Grid } from "@mui/material";
import { useState } from "react";
import { useParams } from "react-router-dom";

import Flex from "../../../../components/Flex";
import Text from "../../../../components/Text";
import StyledButtonV2 from "../../../../components/app/StyledButtonV2";
import { ScrollArea } from "../../../../components/ScrollBar";
import {
  DEFAULT_BORDER_COLOR,
  DEFAULT_LABEL_COLOR_2,
  getXtenantId,
  isAdmin,
} from "../../../../utils/utils";
import { useGetConfigurationDetailsByName } from "../../../../queries/practice-setting/configuration/use-get-config-details-by-name";
import { useGetProviderDetailsByName } from "../../../../queries/practice-setting/configuration/use-get-provider-details-by-name";

import CallerIdVerificationForm from "./practice-setting/CallerIdVerificationForm";
import EmailIdVerificationForm from "./practice-setting/EmailIdVerificationForm";
import PatientIdConfiguration from "./practice-setting/patient-id-config/PatientIdConfiguration";
import ClaimIntegrationDetails from "./practice-setting/patient-id-config/ClaimIntegrationDetails";
import FaxIntegrationDetails from "./fax-integration/FaxIntegrationDetails";
import AppointmentConfigurationForm from "./practice-setting/AppointmentConfigurationForm";
import InvoiceReminderConfigurationForm from "./practice-setting/InvoiceReminderConfigurationForm";
import { CHART_COLORS } from "../../../../components/app/provider/patient/chart";

type SubTabValue =
  // | "general"
  | "patient-management"
  | "communication"
  | "pharmacy"
  | "labs"
  | "invoice"
  | "appointment"
  | "payer-claims";

type SubTab = {
  value: SubTabValue;
  label: string;
};

const CONFIG_SUB_TABS: SubTab[] = [
  // { value: "general", label: "General" },
  { value: "patient-management", label: "Patient Management" },
  { value: "communication", label: "Communication" },
  // { value: "pharmacy", label: "Pharmacy" },
  // { value: "labs", label: "Labs" },
  { value: "invoice", label: "Invoice" },
  { value: "appointment", label: "Appointment" },
  { value: "payer-claims", label: "Payer & Claims" },
];

const ProviderConfig = () => {
  const params = useParams();
  const isAdminSide = isAdmin();
  const [activeSubTab, setActiveSubTab] =
    useState<SubTabValue>("patient-management");

  const { data: providerDetails } = useGetProviderDetailsByName({
    providerGroupName: getXtenantId() ?? params?.providerGroupId ?? "",
  });
  const { data: configurationDetails, refetch } =
    useGetConfigurationDetailsByName({
      providerGroupName: getXtenantId() ?? params?.providerGroupId ?? "",
    });

  const renderEmptyState = () => (
    <Flex
      flexDirection={"column"}
      alignItems={"center"}
      justifyContent={"center"}
      gap={"16px"}
      height={"50vh"}
      width={"100%"}
    >
      <Text fontSize={"14px"} color={DEFAULT_LABEL_COLOR_2}>
        No configuration available.
      </Text>
      <StyledButtonV2
        label="Add Integration"
        shouldAddIcon
        onClick={() => undefined}
      />
    </Flex>
  );

  const renderContent = () => {
    switch (activeSubTab) {
      case "patient-management":
        return (
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <PatientIdConfiguration />
            </Grid>
          </Grid>
        );

      case "communication":
        return (
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <CallerIdVerificationForm
                providerDetails={providerDetails}
                configurationDetails={configurationDetails}
                onSetCallerId={() => refetch()}
              />
            </Grid>
            <Grid item xs={4}>
              <EmailIdVerificationForm
                onVerify={() => refetch()}
                providerDetails={providerDetails}
                configurationDetails={configurationDetails}
              />
            </Grid>
            <Grid item xs={4} md={4}>
              <FaxIntegrationDetails />
            </Grid>
          </Grid>
        );

      case "payer-claims":
        return (
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <ClaimIntegrationDetails />
            </Grid>
          </Grid>
        );

      case "appointment":
        return (
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <AppointmentConfigurationForm />
            </Grid>
          </Grid>
        );

      case "invoice":
        return (
          <Grid container spacing={2}>
            <Grid item xs={4}>
              <InvoiceReminderConfigurationForm />
            </Grid>
          </Grid>
        );

      // case "general":
      case "pharmacy":
      case "labs":
      default:
        return renderEmptyState();
    }
  };

  return (
    <ScrollArea
      height={`calc(100vh - ${isAdminSide ? 20 : 12}rem)`}
      width={"100%"}
    >
      <Flex
        gap={"8px"}
        alignItems={"center"}
        flexWrap={"wrap"}
        borderRadius={"4px"}
        maxWidth={"fit-content"}
        sx={{
          borderBottom: `1px solid ${DEFAULT_BORDER_COLOR}`,
          padding: "4px",
          marginBottom: "16px",
          backgroundColor: CHART_COLORS.settingsConfigSubNavbg,
        }}
      >
        {CONFIG_SUB_TABS.map((tab) => {
          const isActive = activeSubTab === tab.value;
          return (
            <Box
              key={tab.value}
              role="tab"
              aria-selected={isActive}
              onClick={() => setActiveSubTab(tab.value)}
              sx={{
                cursor: "pointer",
                padding: "6px 14px",
                borderRadius: "4px",
                fontSize: "13px",
                fontWeight: isActive ? 600 : 500,
                color: isActive
                  ? CHART_COLORS.settingsConfigSubnavTitleColor
                  : DEFAULT_LABEL_COLOR_2,
                backgroundColor: isActive
                  ? CHART_COLORS.settingsConfigTitleBg
                  : "transparent",
                border: `1px solid ${isActive ? DEFAULT_BORDER_COLOR : "transparent"}`,
                boxShadow: isActive
                  ? "0px 1px 2px rgba(16, 24, 40, 0.05)"
                  : "none",
                transition: "all 0.15s ease-in-out",
                "&:hover": {
                  color: CHART_COLORS.settingsConfigSubnavTitleColor,
                },
              }}
            >
              {tab.label}
            </Box>
          );
        })}
      </Flex>

      <Box>{renderContent()}</Box>
    </ScrollArea>
  );
};

export default ProviderConfig;
